class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';
    this.sectionId = this.dataset.sectionId || 'cart-drawer';
    this.busy = false;

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => this.onKeydown(event));

    this.onDocumentClick = (event) => {
      const opener = event.target.closest('[data-cart-open]');
      if (!opener) return;
      event.preventDefault();
      this.open();
    };
    document.addEventListener('click', this.onDocumentClick);

    this.onCartUpdated = (event) => {
      if (event.detail?.source === 'cart-drawer') return;
      this.refresh({ openAfter: true });
    };
    document.addEventListener('cart:updated', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('cart:updated', this.onCartUpdated);
    this.releaseInert();
  }

  get panel() {
    return this.querySelector('.cart-drawer__panel');
  }

  open() {
    if (!this.hidden) return;
    this.returnFocus = document.activeElement;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.body.classList.add('cart-drawer-open');
    this.inerted = [...document.body.children].filter((element) => !element.contains(this) && element.tagName !== 'SCRIPT');
    this.inerted.forEach((element) => { element.inert = true; });
    (this.querySelector('[data-drawer-close]:not(.cart-drawer__scrim)') || this.panel)?.focus();
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    this.releaseInert();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => { this.hidden = true; }, reduceMotion ? 0 : 320);
    this.returnFocus?.focus?.();
  }

  releaseInert() {
    this.inerted?.forEach((element) => { element.inert = false; });
    this.inerted = [];
  }

  setBusy(busy) {
    this.busy = busy;
    this.panel?.setAttribute('aria-busy', String(busy));
    this.classList.toggle('is-busy', busy);
  }

  setStatus(message) {
    const status = this.querySelector('[data-drawer-status]');
    if (status) status.textContent = message || '';
  }

  async refresh({ openAfter = false } = {}) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}?sections=${this.sectionId}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to refresh the bag.');
      const sections = await response.json();
      const markup = sections[this.sectionId];
      if (!markup) throw new Error('Unable to refresh the bag.');
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const nextBody = parsed.querySelector('[data-drawer-body]');
      const currentBody = this.querySelector('[data-drawer-body]');
      if (nextBody && currentBody) {
        const hadFocusInside = currentBody.contains(document.activeElement);
        currentBody.replaceWith(nextBody);
        if (hadFocusInside && !this.hidden) this.panel?.focus();
      }
      if (openAfter) this.open();
    } catch (error) {
      this.setStatus(error.message);
    }
  }

  announceUpdate() {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer' } }));
  }

  async changeLine(key, quantity) {
    if (this.busy) return;
    this.setBusy(true);
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'Unable to update your bag.');
      }
      await this.refresh();
      this.announceUpdate();
    } catch (error) {
      this.setStatus(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  async addGift(variantId, tier) {
    if (this.busy || !variantId) return;
    this.setBusy(true);
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: { _free_gift: tier } }] })
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'Unable to add this gift.');
      }
      await this.refresh();
      this.announceUpdate();
    } catch (error) {
      this.setStatus(error.message);
    } finally {
      this.setBusy(false);
    }
  }

  onClick(event) {
    const close = event.target.closest('[data-drawer-close]');
    if (close) {
      this.close();
      return;
    }
    const adjust = event.target.closest('[data-line-adjust]');
    if (adjust) {
      const quantity = Number(adjust.dataset.lineQty) + Number(adjust.dataset.lineAdjust);
      this.changeLine(adjust.dataset.lineKey, Math.max(0, quantity));
      return;
    }
    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      this.changeLine(remove.dataset.lineKey, 0);
      return;
    }
    const gift = event.target.closest('[data-gift-add]');
    if (gift) this.addGift(gift.dataset.variantId, gift.dataset.giftTier);
  }

  onKeydown(event) {
    if (this.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
